export function formatUnixTimestamp(unixTimestamp) {
  const date = new Date(unixTimestamp * 1000) // Convert seconds to milliseconds

  // Format options for US users
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }

  return new Intl.DateTimeFormat("en-US", options).format(date)
}

// Sort Utility
export const sortData = (data, sortConfig) => {
  if (sortConfig?.key) {
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key]?.toString().toLowerCase() || ""
      const bValue = b[sortConfig.key]?.toString().toLowerCase() || ""
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1
      return 0
    })
  }
  return data
}

// Pagination Utility
export const paginateData = (data, currentPage, itemsPerPage) => {
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = currentPage * itemsPerPage
  return data.slice(startIndex, endIndex)
}

export function validateFaxNumber(faxNumber) {
  const cleanedFaxNumber = faxNumber.replace(/[^\d]/g, "")

  const regex = /^(1[2-9][0-9]{9}|[2-9][0-9]{9})$/

  if (regex.test(cleanedFaxNumber)) {
    return true
  }

  return false
}

export function validateFaxNumberOnlyDigit(fax) {
  const faxTrim = fax.trim()
  let faxNumber = faxTrim.replace(/-/g, "")
  faxNumber = faxNumber.trim().replace(/\+/g, "")

  const length = faxNumber.length
  const firstDigit = faxNumber.charAt(0)
  const secondDigit = faxNumber.charAt(1)

  const hasPlusNotAtStart = /(?<!^)\+/.test(faxTrim)

  if (!/^\d+$/.test(faxNumber) || hasPlusNotAtStart) {
    return "Fax number must contain digits  or initial `+` or `-` only"
  }

  if (fax.startsWith("+") && !/^\+\d/.test(fax)) {
    return "Invalid fax number! Second char must be a digit"
  }

  // For International numbers
  if (faxTrim.startsWith("+") && !faxTrim.startsWith("+1")) {
    if (faxTrim.startsWith("+") && faxTrim.startsWith("+0")) {
      return "For international number, first digit must be > 1"
    }

    if (length < 8 || length > 19) {
      return "For international number, faxnumber length must be more then 7 and less then 20 digits"
    }
  } else {
    // For Domestic US numbers
    if (length !== 10 && length !== 11) {
      return "Invalid fax number! Length must be of 10 or 11 digits"
    }

    if (length === 10 && parseInt(firstDigit) <= 1) {
      return "Invalid fax number! Given length is 10 digits, so first digit must be > 1"
    }

    if (length === 11 && parseInt(firstDigit) !== 1) {
      return "Invalid fax number! Given length is 11 digits, so first digit must be 1"
    }

    if (length === 11 && parseInt(secondDigit) <= 1) {
      return "Invalid fax number! Given length is 11 digits, so second digit must be > 1"
    }
  }

  return false
}

export function prepareFinalFaxNumber(faxNumber) {
  let internationalNumber = false
  if (faxNumber.startsWith("+") && Number(faxNumber[1]) > 1) {
    internationalNumber = true
  }

  const clearFaxNumber = faxNumber.trim().replace(/[-]/g, "")

  return clearFaxNumber.length === 10 && internationalNumber === false
    ? `1${clearFaxNumber}`
    : clearFaxNumber
}

// prepareFinalFaxNumberForPhonebook
export function prepareFinalFaxNumberForPhonebook(faxNumber) {
  let value = faxNumber.trim()

  // Always remove hyphens
  value = value.replace(/-/g, "")

  // Case 1: + followed by digit > 1 (international)
  if (value.startsWith("+") && Number(value[1]) > 1) {
    return faxNumber.trim() // keep +, don't add 1
  }

  // Case 2: +1 (US) → remove +
  if (value.startsWith("+1")) {
    value = value.slice(1)
  }

  // Case 3: 10-digit US number → prepend 1
  if (/^\d{10}$/.test(value)) {
    return `1${value}`
  }

  return value
}

export const formatFaxNumber = (rawNumber) => {
  if (isEmpty(rawNumber)) {
    return ""
  }

  // if (!rawNumber) {
  //   return rawNumber // fallback for invalid input
  // }

  let formatedNumber = ""

  if (
    /[^0-9_-]/.test(rawNumber.trim()) ||
    !rawNumber ||
    (rawNumber.length !== 10 && rawNumber.length !== 11)
  ) {
    formatedNumber = rawNumber
  } else if (rawNumber.length === 10) {
    const areaCode = rawNumber.slice(0, 3)
    const prefix = rawNumber.slice(3, 6)
    const lineNumber = rawNumber.slice(6)

    formatedNumber = `${areaCode}-${prefix}-${lineNumber}`
  } else if (rawNumber.length === 11) {
    const countryCode = rawNumber.slice(0, 1)
    const areaCode = rawNumber.slice(1, 4)
    const prefix = rawNumber.slice(4, 7)
    const lineNumber = rawNumber.slice(7)

    formatedNumber = `${countryCode}-${areaCode}-${prefix}-${lineNumber}`
  }

  return formatedNumber
}

export function isEmpty(value, checkNullText = false) {
  // null or undefined
  if (value == null) return true

  if (value === "") return true

  if (checkNullText) {
    if (value === "null") return true
    if (value === "NULL") return true
    if (value.toLowerCase() === "unknown") return true
  }

  // Boolean or Number (including NaN) are never "empty"
  if (typeof value === "boolean" || typeof value === "number") return false

  // String: check length
  if (typeof value === "string") return value.length === 0

  // Array: check length
  if (Array.isArray(value)) return value.length === 0

  // Map or Set: check size
  if (value instanceof Map || value instanceof Set) return value.size === 0

  // Object: check own enumerable keys
  if (typeof value === "object") return Object.keys(value).length === 0

  // Fallback
  return false
}

export const customSelectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 0,
    minWidth: 300,
    height: 41, // fixed height
    minHeight: 41,
    zIndex: 1000, // keep control above popup if needed
  }),
  valueContainer: (base) => ({
    ...base,
    justifyContent: "flex-start",
    paddingLeft: 8,
  }),
  singleValue: (base) => ({
    ...base,
    textAlign: "left",
    marginLeft: 0,
  }),
  placeholder: (base) => ({
    ...base,
    textAlign: "left",
    marginLeft: 0,
  }),
  menu: (base) => ({
    ...base,
    textAlign: "left",
    maxHeight: 200, // limit dropdown height
    overflowY: "auto", // enable scrolling
    zIndex: 2000, // ensure it renders above popup
    position: "absolute", // force it to overlay
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 200, // limit dropdown height
    overflowY: "auto", // 👈 enables mouse wheel scroll
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2000, // critical: ensures dropdown is above modals/popups
  }),
  option: (base, state) => ({
    ...base,
    textAlign: "left",
    backgroundColor: state.isSelected
      ? "#eee"
      : state.isFocused
        ? "#f5f5f5"
        : "white",
    color: "black",
  }),
}

export const getFaxNumberToDisplayInbound = (fax) => {
  return !isEmpty(fax?.originating_fax_tsid, true)
    ? fax?.originating_fax_tsid
    : !isEmpty(fax?.originating_fax_number, true)
      ? fax?.originating_fax_number
      : "unknown"
}

export const getFaxNumberToDisplayOutbound = (fax) => {
  return !isEmpty(fax?.destination_fax_csid, true)
    ? fax?.destination_fax_csid
    : !isEmpty(fax?.destination_fax_number, true)
      ? fax?.destination_fax_number
      : "unknown"
}
